import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { QueryOrdering, TimeRangeConfig } from '@/api/generated/model';
import { useDebounceValue } from '@/hooks/use-debounce';
import { useAvailableTags } from '@/hooks/use-available-tags';
import { useGlobalFiltersContext } from '@/contexts/filters/filter-context';
import { addTagPrefix } from '@/models/common/filters';
import { FilterOperator } from '@/comps/FilterPicker/filter-operators';
import { AvailableValue, GenericFilter, VolumeKind } from '@/comps/FilterPicker/FilterPicker';
import { getVariable, useGetFilterValues } from '@/comps/FilterPicker/utils';
import { FilterContentV2ValueDropdown } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2ValueDropdown';
import { LastInteractionTagFilterState } from '@/comps/FilterPicker/filter-content-v2/lastInteractionTagFilterAdapter';
import { List, ListItem } from '@/comps/list';
import { Button } from '@/comps/primitives/button';
import { humanizeString } from '@/utils/string.utils';
import { CheckedState } from '@radix-ui/react-checkbox';
import { cn } from '@/utils/shad';
import { FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS } from '@/comps/FilterPicker/filter-content-v2/constants';
type Props = {
  operator: FilterOperator;
  initialState: LastInteractionTagFilterState;
  projectId: string;
  timeRange?: TimeRangeConfig;
  volumeKind?: VolumeKind;
  filters: GenericFilter[];
  onChange: (state: LastInteractionTagFilterState) => void;
  onApply: (state: LastInteractionTagFilterState) => void;
};
function cloneState(state: LastInteractionTagFilterState): LastInteractionTagFilterState {
  return {
    ...state,
    values: [...state.values]
  };
}
export function FilterContentV2LastInteractionTagValuePopover(t0) {
  const $ = _c(95);
  const {
    operator,
    initialState,
    projectId,
    timeRange,
    volumeKind,
    filters,
    onChange,
    onApply
  } = t0;
  let t1;
  if ($[0] !== initialState) {
    t1 = () => cloneState(initialState);
    $[0] = initialState;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [state, setState] = useState(t1);
  const [searchFilterValue, setSearchFilterValue] = useState("");
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      by: getVariable(),
      direction: "desc"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [sortBy, setSortBy] = useState(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [allValuesSnapshot, setAllValuesSnapshot] = useState(t3);
  const {
    aggregateBy
  } = useGlobalFiltersContext();
  const debouncedSearchFilterValue = useDebounceValue(searchFilterValue);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    availableTags,
    isLoading: isLoadingTags
  } = useAvailableTags(projectId, t4, timeRange);
  let t5;
  if ($[5] !== state.tag) {
    t5 = state.tag ? addTagPrefix(state.tag) : null;
    $[5] = state.tag;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const tagColumn = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {};
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = !!tagColumn && operator !== "contains";
  let t8;
  if ($[8] !== aggregateBy || $[9] !== debouncedSearchFilterValue || $[10] !== filters || $[11] !== projectId || $[12] !== sortBy || $[13] !== state.select_all || $[14] !== t7 || $[15] !== tagColumn || $[16] !== timeRange || $[17] !== volumeKind) {
    t8 = {
      filters,
      filterKind: tagColumn,
      timeRange,
      projectId,
      search: debouncedSearchFilterValue,
      filtersMapByKind: t6,
      isSelectedAll: state.select_all,
      volumeKind,
      sortBy,
      aggregateBy,
      enabled: t7
    };
    $[8] = aggregateBy;
    $[9] = debouncedSearchFilterValue;
    $[10] = filters;
    $[11] = projectId;
    $[12] = sortBy;
    $[13] = state.select_all;
    $[14] = t7;
    $[15] = tagColumn;
    $[16] = timeRange;
    $[17] = volumeKind;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const {
    availableFilterValues: tempAvailableFilterValues,
    isLoading: isLoadingValues
  } = useGetFilterValues(t8);
  const availableFilterValues = tempAvailableFilterValues;
  const showSelectAll = (availableFilterValues?.length ?? 0) > 1;
  const hasSelectedAll = !!state.select_all;
  let t9;
  if ($[19] !== initialState) {
    t9 = () => {
      setState(cloneState(initialState));
    };
    $[19] = initialState;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== initialState.operator || $[22] !== initialState.search || $[23] !== initialState.select_all || $[24] !== initialState.tag || $[25] !== initialState.values) {
    t10 = [initialState.tag, initialState.operator, initialState.values, initialState.select_all, initialState.search];
    $[21] = initialState.operator;
    $[22] = initialState.search;
    $[23] = initialState.select_all;
    $[24] = initialState.tag;
    $[25] = initialState.values;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[27] !== onChange || $[28] !== state) {
    t11 = () => {
      onChange(state);
    };
    t12 = [onChange, state];
    $[27] = onChange;
    $[28] = state;
    $[29] = t11;
    $[30] = t12;
  } else {
    t11 = $[29];
    t12 = $[30];
  }
  useEffect(t11, t12);
  let t13;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setAllValuesSnapshot([]);
    };
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== state.tag) {
    t14 = [state.tag];
    $[32] = state.tag;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[34] !== availableFilterValues || $[35] !== hasSelectedAll) {
    t15 = () => {
      if (hasSelectedAll && (availableFilterValues?.length ?? 0) > 0) {
        setAllValuesSnapshot(prev => prev.length === 0 ? availableFilterValues?.map(_temp) ?? [] : prev);
      }
      if (!hasSelectedAll) {
        setAllValuesSnapshot(_temp2);
      }
    };
    t16 = [availableFilterValues, hasSelectedAll];
    $[34] = availableFilterValues;
    $[35] = hasSelectedAll;
    $[36] = t15;
    $[37] = t16;
  } else {
    t15 = $[36];
    t16 = $[37];
  }
  useEffect(t15, t16);
  let t17;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = patch => {
      setState(current => ({
        ...current,
        ...patch
      }));
    };
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const updateState = t17;
  let t18;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = tag => {
      setState(current_0 => ({
        ...current_0,
        tag,
        values: [],
        select_all: false,
        search: undefined
      }));
      setSearchFilterValue("");
      setAllValuesSnapshot([]);
    };
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const handleSelectTag = t18;
  let t19;
  if ($[40] !== availableFilterValues) {
    t19 = checked => {
      if (checked) {
        const values = availableFilterValues?.slice(0, Math.min(availableFilterValues.length, 5)).map(_temp3) ?? [];
        updateState({
          select_all: true,
          values,
          search: undefined
        });
        return;
      }
      updateState({
        select_all: false,
        values: []
      });
    };
    $[40] = availableFilterValues;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  const handleCheckSelectAll = t19;
  let t20;
  if ($[42] !== allValuesSnapshot || $[43] !== hasSelectedAll || $[44] !== state.select_all || $[45] !== state.values) {
    t20 = (value, checked_0) => {
      const baseValues = hasSelectedAll ? allValuesSnapshot : state.values;
      const valuesSet = new Set(baseValues);
      if (checked_0) {
        valuesSet.add(value.value);
      } else {
        valuesSet.delete(value.value);
      }
      updateState({
        values: Array.from(valuesSet),
        select_all: hasSelectedAll ? false : state.select_all,
        search: undefined
      });
    };
    $[42] = allValuesSnapshot;
    $[43] = hasSelectedAll;
    $[44] = state.select_all;
    $[45] = state.values;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  const handleSelectFilterValue = t20;
  let t21;
  if ($[47] !== sortBy.direction) {
    t21 = by => {
      const newDirection = sortBy.direction === "asc" ? "desc" : "asc";
      setSortBy({
        by,
        direction: newDirection
      });
    };
    $[47] = sortBy.direction;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const handleChangeSortByDirection = t21;
  let t22;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = by_0 => {
      const isAlpha = by_0 === "alphabetical";
      setSortBy({
        by: by_0,
        direction: isAlpha ? "asc" : "desc"
      });
    };
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  const handleChangeSortBy = t22;
  let t23;
  bb0: {
    if (!state.tag) {
      t23 = false;
      break bb0;
    }
    if (operator === "contains") {
      let t24;
      if ($[50] !== state.search) {
        t24 = state.search?.trim();
        $[50] = state.search;
        $[51] = t24;
      } else {
        t24 = $[51];
      }
      t23 = !!t24;
      break bb0;
    }
    t23 = !!state.select_all || state.values.length > 0;
  }
  const canApply = t23;
  let t24;
  if ($[52] !== availableTags?.tags) {
    t24 = availableTags?.tags ?? [];
    $[52] = availableTags?.tags;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  const tagOptions = t24;
  let t25;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <p className="text-xs font-semibold text-zinc-400">Tag</p>;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== isLoadingTags || $[56] !== state.tag || $[57] !== tagOptions) {
    t26 = <div className="flex flex-col gap-2">{t25}{isLoadingTags ? <p className="text-xs text-zinc-500">Loading tags...</p> : <List className={cn(FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS, "max-h-40 overflow-y-auto p-1")}>{tagOptions.map(tag_0 => <ListItem key={tag_0} className={cn("cursor-pointer rounded-[8px] p-2 text-xs text-white hover:bg-neutral-800", state.tag === tag_0 && "bg-neutral-800")} onClick={() => handleSelectTag(tag_0)}>{humanizeString(tag_0)}</ListItem>)}</List>}</div>;
    $[55] = isLoadingTags;
    $[56] = state.tag;
    $[57] = tagOptions;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== handleChangeSortByDirection || $[60] !== onApply || $[61] !== operator || $[62] !== searchFilterValue || $[63] !== sortBy || $[64] !== state || $[65] !== tagColumn || $[66] !== volumeKind) {
    t27 = state.tag && operator === "contains" && <FilterContentV2ValueDropdown filterKind={tagColumn} operator={operator} containsText={state.search ?? ""} onContainsTextChange={text => updateState({
      search: text,
      values: [],
      select_all: false
    })} onApplyContains={() => onApply({
      ...state,
      operator
    })} searchFilterValue={searchFilterValue} setSearchFilterValue={setSearchFilterValue} sortBy={sortBy} onChangeSortBy={handleChangeSortBy} onChangeSortByDirection={handleChangeSortByDirection} availableFilterValues={undefined} isLoading={false} targetFilterValues={[]} hasSelectedAll={false} showSelectAll={false} onCheckSelectAll={_temp4} onSelectFilterValue={_temp5} volumeKind={volumeKind} />;
    $[59] = handleChangeSortByDirection;
    $[60] = onApply;
    $[61] = operator;
    $[62] = searchFilterValue;
    $[63] = sortBy;
    $[64] = state;
    $[65] = tagColumn;
    $[66] = volumeKind;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== availableFilterValues || $[69] !== handleChangeSortByDirection || $[70] !== handleCheckSelectAll || $[71] !== handleSelectFilterValue || $[72] !== hasSelectedAll || $[73] !== isLoadingValues || $[74] !== operator || $[75] !== searchFilterValue || $[76] !== showSelectAll || $[77] !== sortBy || $[78] !== state.tag || $[79] !== state.values || $[80] !== tagColumn || $[81] !== volumeKind) {
    t28 = state.tag && operator !== "contains" && <FilterContentV2ValueDropdown filterKind={tagColumn} operator={operator} containsText="" onContainsTextChange={_temp6} onApplyContains={_temp7} searchFilterValue={searchFilterValue} setSearchFilterValue={setSearchFilterValue} sortBy={sortBy} onChangeSortBy={handleChangeSortBy} onChangeSortByDirection={handleChangeSortByDirection} availableFilterValues={availableFilterValues} isLoading={isLoadingValues} targetFilterValues={state.values} hasSelectedAll={hasSelectedAll} showSelectAll={showSelectAll} onCheckSelectAll={handleCheckSelectAll} onSelectFilterValue={handleSelectFilterValue} volumeKind={volumeKind} />;
    $[68] = availableFilterValues;
    $[69] = handleChangeSortByDirection;
    $[70] = handleCheckSelectAll;
    $[71] = handleSelectFilterValue;
    $[72] = hasSelectedAll;
    $[73] = isLoadingValues;
    $[74] = operator;
    $[75] = searchFilterValue;
    $[76] = showSelectAll;
    $[77] = sortBy;
    $[78] = state.tag;
    $[79] = state.values;
    $[80] = tagColumn;
    $[81] = volumeKind;
    $[82] = t28;
  } else {
    t28 = $[82];
  }
  const t29 = !canApply;
  let t30;
  if ($[83] !== onApply || $[84] !== operator || $[85] !== state) {
    t30 = () => onApply({
      ...state,
      operator
    });
    $[83] = onApply;
    $[84] = operator;
    $[85] = state;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  let t31;
  if ($[87] !== t29 || $[88] !== t30) {
    t31 = <div className="flex justify-end"><Button type="button" className="h-7 gap-1.5 rounded-base bg-primary/80 px-2 text-[12px] font-semibold text-[#101010] hover:bg-primary/70" disabled={t29} onClick={t30}>Apply filter</Button></div>;
    $[87] = t29;
    $[88] = t30;
    $[89] = t31;
  } else {
    t31 = $[89];
  }
  let t32;
  if ($[90] !== t26 || $[91] !== t27 || $[92] !== t28 || $[93] !== t31) {
    t32 = <div className="w-auto max-w-96 overflow-hidden rounded-md border border-zinc-800 bg-[#09090B] shadow-md"><div className="flex flex-col gap-3 p-3">{t26}{t27}{t28}{t31}</div></div>;
    $[90] = t26;
    $[91] = t27;
    $[92] = t28;
    $[93] = t31;
    $[94] = t32;
  } else {
    t32 = $[94];
  }
  return t32;
}
function _temp7() {}
function _temp6() {}
function _temp5() {}
function _temp4() {}
function _temp3(entry_0) {
  return entry_0.value;
}
function _temp2(prev_0) {
  return prev_0.length === 0 ? prev_0 : [];
}
function _temp(entry) {
  return entry.value;
}